import React, { useState, useEffect } from 'react';
import { fetchLots, updateLot } from '../../services/api';

const LotsManagement = ({ lots = [], fetchLots }) => {
  const [lotsData, setLotsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLot, setSelectedLot] = useState(null);
  const [formData, setFormData] = useState({
    lotNumber: '',
    type: '',
    size: '',
    price: '',
    status: 'available',
    description: ''
  });

  // Load lots data when component mounts
  useEffect(() => {
    const loadLots = async () => {
      try {
        setLoading(true);
        
        // If lots were passed as props, use them
        if (Array.isArray(lots) && lots.length > 0) {
          setLotsData(lots);
          setLoading(false);
          return;
        }
        
        // Otherwise fetch lots directly
        const data = await fetchLots();
        setLotsData(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching lots:', err);
        setError('Failed to load lots. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    loadLots();
  }, [lots, fetchLots]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission to create/update lot
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (selectedLot) {
        // Update existing lot
        const updatedLot = await updateLot(selectedLot._id, formData);
        console.log('Lot updated successfully:', updatedLot);
        
        // Update the lot in the local state to prevent it from disappearing
        setLotsData(prevLots => {
          return prevLots.map(lot => 
            lot._id === selectedLot._id ? { ...lot, ...formData } : lot
          );
        });
        
        alert('Lot updated successfully');
      } else {
        // Create new lot logic would go here
        alert('Lot creation functionality to be implemented');
      }
      
      // Refresh lots data by calling the fetchLots function
      if (typeof fetchLots === 'function') {
        const freshData = await fetchLots();
        if (Array.isArray(freshData)) {
          setLotsData(freshData);
        }
      }
      
      // Reset form and selected lot
      setSelectedLot(null);
      setFormData({
        lotNumber: '',
        type: '',
        size: '',
        price: '',
        status: 'available',
        description: ''
      });
    } catch (err) {
      console.error('Error managing lot:', err);
      setError('Failed to save lot. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Select a lot for editing
  const handleEdit = (lot) => {
    setSelectedLot(lot);
    setFormData({
      lotNumber: lot.lotNumber || '',
      type: lot.type || '',
      size: lot.size || '',
      price: lot.price || '',
      status: lot.status || 'available',
      description: lot.description || ''
    });
  };

  // Cancel editing
  const handleCancel = () => {
    setSelectedLot(null);
    setFormData({
      lotNumber: '',
      type: '',
      size: '',
      price: '',
      status: 'available',
      description: ''
    });
  };

  // Get status badge class based on status
  const getStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case 'sold':
        return 'status-sold';
      case 'reserved':
        return 'status-reserved';
      case 'available':
      default:
        return 'status-available';
    }
  };

  if (loading && lotsData.length === 0) {
    return <div className="loading-indicator">Loading lots data...</div>;
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  return (
    <div className="lots-management-container">
      <div className="admin-section">
        <h3>{selectedLot ? 'Edit Lot' : 'Add New Lot'}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Lot Number</label>
            <input
              type="text"
              name="lotNumber"
              value={formData.lotNumber}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label>Type</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              required
            >
              <option value="">Select Type</option>
              <option value="Factory">Factory</option>
              <option value="Warehouse">Warehouse</option>
              <option value="Office">Office Space</option>
              <option value="Retail">Retail Space</option>
            </select>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>Size (sqft)</label>
              <input
                type="number"
                name="size"
                value={formData.size}
                onChange={handleChange}
              />
            </div>
            
            <div className="form-group">
              <label>Price</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
              />
            </div>
          </div>
          
          <div className="form-group">
            <label>Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
            >
              <option value="available">Available</option>
              <option value="reserved">Reserved</option>
              <option value="sold">Sold</option>
            </select>
          </div>
          
          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="4"
            ></textarea>
          </div>
          
          <div className="form-actions">
            <button type="submit" className="btn-primary">
              {selectedLot ? 'Update Lot' : 'Add Lot'}
            </button>
            
            {selectedLot && (
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCancel}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
      
      <div className="admin-section">
        <h3>Manage Lots</h3>
        
        {lotsData.length === 0 ? (
          <p>No lots found. Add your first lot using the form above.</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Lot Number</th>
                <th>Type</th>
                <th>Size</th>
                <th>Price</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {lotsData.map(lot => (
                <tr key={lot._id}>
                  <td>{lot.lotNumber}</td>
                  <td>{lot.type}</td>
                  <td>{lot.size} sqft</td>
                  <td>${parseFloat(lot.price).toLocaleString()}</td>
                  <td>
                    <span className={`status-badge ${getStatusClass(lot.status)}`}>
                      {lot.status}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn-secondary"
                      onClick={() => handleEdit(lot)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default LotsManagement; 